/**
 * TopdownViewport — plan view for `topdown-2d` floor plans (design §15). Rooms as
 * rectangles (world x/y/w/h), corridors as connections between room centers (thickness ∝
 * width; locked doors marked with their key id), keys as dots, start/goal badges, red
 * overlay on validator errors. Zoom (wheel), pan (drag empty space), click room/corridor to
 * select, drag a room to move it. Same Canvas-2D seam as LevelViewport (Three.js-ortho swap
 * pending dep limits).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TopdownLevel, LevelFinding } from '@volley/crucible-levels';
import type { PrevisSkin } from './previs-skin';

export type TopdownSelection = { type: 'room'; id: string } | { type: 'corridor'; index: number };

const COLORS = {
  bg: '#0f1216',
  room: 'rgba(133,183,235,0.12)',
  roomEdge: '#85b7eb',
  start: '#5dcaa5',
  goal: '#efa027',
  boss: '#e24b4a',
  corridor: 'rgba(255,255,255,0.45)',
  locked: '#efa027',
  key: '#f0c000',
  bad: '#e24b4a',
  sel: '#f0c000',
  label: 'rgba(255,255,255,0.7)',
};

function edgeColor(kind: string, c: typeof COLORS = COLORS): string {
  if (kind === 'start') return c.start;
  if (kind === 'goal') return c.goal;
  if (kind === 'boss') return c.boss;
  return c.roomEdge;
}

/** Skin-aware plan-view colors (design §19) — schematic defaults when no skin. */
function colorsFor(skin: PrevisSkin | null | undefined): typeof COLORS {
  const pal = skin?.palette;
  if (!pal) return COLORS;
  return {
    ...COLORS,
    bg: pal.sky,
    room: `${pal.roomFloor}22`,
    roomEdge: pal.roomFloor,
    boss: pal.hazard,
    corridor: pal.corridor,
    locked: pal.lock,
    key: pal.key,
  };
}

export function TopdownViewport({
  level,
  findings,
  selected,
  onMoveRoom,
  onSelect,
  skin,
}: {
  level: TopdownLevel;
  findings: LevelFinding[];
  selected?: TopdownSelection | null;
  /** Drag a room → new min-corner position (m, snapped to 0.5). */
  onMoveRoom?: (roomId: string, x: number, y: number) => void;
  onSelect?: (sel: TopdownSelection | null) => void;
  /** Previs palette (design §19) — renders in the game's look when set. */
  skin?: PrevisSkin | null;
}): React.ReactElement {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 800, h: 380 });
  const view = useRef({ zoom: 12, panX: 40, panY: 40, fitKey: '' });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const dragRoom = useRef<{ id: string; offX: number; offY: number } | null>(null);
  const downPos = useRef<{ x: number; y: number } | null>(null);
  const movedFar = useRef(false);
  const [, forceDraw] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ w: Math.max(200, r.width), h: Math.max(200, r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const badRooms = new Set(
    findings.filter((f) => f.severity === 'error' && f.at?.roomId).map((f) => f.at!.roomId!),
  );
  const badCorridors = new Set(
    findings
      .filter((f) => f.severity === 'error' && f.at?.corridorIndex !== undefined)
      .map((f) => f.at!.corridorIndex!),
  );
  const roomById = new Map(level.rooms.map((r) => [r.id, r]));
  const center = (id: string): { x: number; y: number } => {
    const r = roomById.get(id);
    return r ? { x: r.x + r.w / 2, y: r.y + r.h / 2 } : { x: 0, y: 0 };
  };
  const keysByRoom = new Map<string, string[]>();
  for (const k of level.keys) {
    keysByRoom.set(k.roomId, [...(keysByRoom.get(k.roomId) ?? []), k.id]);
  }

  // Auto-fit to the plan's extent when the layout identity changes.
  const minX = Math.min(...level.rooms.map((r) => r.x));
  const minY = Math.min(...level.rooms.map((r) => r.y));
  const maxX = Math.max(...level.rooms.map((r) => r.x + r.w));
  const maxY = Math.max(...level.rooms.map((r) => r.y + r.h));
  const fitKey = `${level.rooms.length}:${level.corridors.length}:${size.w}x${size.h}`;
  // Recompute zoom/pan to frame the whole plan. Shared by the auto-fit effect and the
  // "fit" button — the button forces it (fitKey guard bypassed).
  const applyFit = useCallback(() => {
    const worldW = Math.max(maxX - minX, 4);
    const worldH = Math.max(maxY - minY, 4);
    const zoom = Math.max(2, Math.min((size.w - 80) / worldW, (size.h - 80) / worldH, 40));
    view.current = {
      zoom,
      panX: 40 - minX * zoom,
      panY: 40 - minY * zoom,
      fitKey,
    };
    forceDraw((n) => n + 1);
  }, [fitKey, minX, minY, maxX, maxY, size.w, size.h]);
  useEffect(() => {
    if (view.current.fitKey === fitKey) return;
    applyFit();
  }, [fitKey, applyFit]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const { zoom, panX, panY } = view.current;
    const sx = (wx: number) => panX + wx * zoom;
    const sy = (wy: number) => panY + wy * zoom; // plan view: +y = down/south
    const C = colorsFor(skin);

    ctx.fillStyle = C.bg;
    ctx.fillRect(0, 0, size.w, size.h);

    // corridors under rooms
    level.corridors.forEach((c, i) => {
      const a = center(c.fromId);
      const b = center(c.toId);
      const bad = badCorridors.has(i);
      ctx.strokeStyle = bad ? C.bad : c.lockedBy ? C.locked : C.corridor;
      ctx.lineWidth = Math.max(2, c.width * zoom * 0.5);
      ctx.setLineDash(c.lockedBy ? [6, 4] : []);
      ctx.beginPath();
      ctx.moveTo(sx(a.x), sy(a.y));
      ctx.lineTo(sx(b.x), sy(b.y));
      ctx.stroke();
      ctx.setLineDash([]);
      if (selected?.type === 'corridor' && selected.index === i) {
        ctx.strokeStyle = C.sel;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sx(a.x), sy(a.y));
        ctx.lineTo(sx(b.x), sy(b.y));
        ctx.stroke();
      }
      if (c.lockedBy) {
        const mx = (sx(a.x) + sx(b.x)) / 2;
        const my = (sy(a.y) + sy(b.y)) / 2;
        ctx.fillStyle = bad ? C.bad : C.locked;
        ctx.font = '10px ui-monospace, monospace';
        ctx.fillText(`🔒${c.lockedBy}`, mx + 4, my - 4);
      }
    });

    // rooms
    for (const r of level.rooms) {
      const bad = badRooms.has(r.id);
      const isSel = selected?.type === 'room' && selected.id === r.id;
      ctx.fillStyle = bad ? 'rgba(226,75,74,0.15)' : C.room;
      ctx.fillRect(sx(r.x), sy(r.y), r.w * zoom, r.h * zoom);
      ctx.strokeStyle = bad ? C.bad : isSel ? C.sel : edgeColor(r.kind, C);
      ctx.lineWidth = isSel ? 3 : 2;
      ctx.strokeRect(sx(r.x), sy(r.y), r.w * zoom, r.h * zoom);
      ctx.fillStyle = C.label;
      ctx.font = '10px ui-monospace, monospace';
      const tag = r.kind !== 'normal' ? ` · ${r.kind.toUpperCase()}` : '';
      ctx.fillText(`${r.id}${tag}`, sx(r.x) + 4, sy(r.y) + 12);
      if (r.hazard) {
        ctx.fillStyle = C.locked;
        ctx.fillText(`⚠ ${r.hazard}`, sx(r.x) + 4, sy(r.y) + 24);
      }
      // keys in this room
      (keysByRoom.get(r.id) ?? []).forEach((kid, ki) => {
        ctx.fillStyle = C.key;
        ctx.beginPath();
        ctx.arc(sx(r.x) + 10 + ki * 14, sy(r.y + r.h) - 10, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = C.label;
        ctx.fillText(kid, sx(r.x) + 16 + ki * 14, sy(r.y + r.h) - 6);
      });
    }
  }, [level, findings, selected, size.w, size.h, badRooms, badCorridors, keysByRoom, skin]);

  useEffect(() => {
    draw();
  }, [draw]);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const v = view.current;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const newZoom = Math.max(2, Math.min(120, v.zoom * factor));
    v.panX = mx - ((mx - v.panX) * newZoom) / v.zoom;
    v.panY = my - ((my - v.panY) * newZoom) / v.zoom;
    v.zoom = newZoom;
    forceDraw((n) => n + 1);
  };

  const worldAt = (mx: number, my: number): { x: number; y: number } => {
    const { zoom, panX, panY } = view.current;
    return { x: (mx - panX) / zoom, y: (my - panY) / zoom };
  };
  const hitRoomId = (mx: number, my: number): string | null => {
    const w = worldAt(mx, my);
    // topmost (last drawn) wins
    for (let i = level.rooms.length - 1; i >= 0; i--) {
      const r = level.rooms[i];
      if (w.x >= r.x && w.x <= r.x + r.w && w.y >= r.y && w.y <= r.y + r.h) return r.id;
    }
    return null;
  };
  const hitCorridorIndex = (mx: number, my: number): number | null => {
    const w = worldAt(mx, my);
    for (let i = 0; i < level.corridors.length; i++) {
      const c = level.corridors[i];
      const a = center(c.fromId);
      const b = center(c.toId);
      // point-to-segment distance in world units
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((w.x - a.x) * dx + (w.y - a.y) * dy) / len2));
      const px = a.x + t * dx;
      const py = a.y + t * dy;
      const dist = Math.hypot(w.x - px, w.y - py);
      if (dist <= Math.max(c.width, 0.6)) return i;
    }
    return null;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    downPos.current = { x: e.clientX, y: e.clientY };
    movedFar.current = false;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect && onMoveRoom) {
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const rid = hitRoomId(mx, my);
      if (rid) {
        const r = roomById.get(rid)!;
        const w = worldAt(mx, my);
        dragRoom.current = { id: rid, offX: w.x - r.x, offY: w.y - r.y };
        return;
      }
    }
    drag.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (
      downPos.current &&
      Math.hypot(e.clientX - downPos.current.x, e.clientY - downPos.current.y) > 4
    ) {
      movedFar.current = true;
    }
    if (dragRoom.current && onMoveRoom && movedFar.current) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const w = worldAt(e.clientX - rect.left, e.clientY - rect.top);
      const nx = Math.round((w.x - dragRoom.current.offX) * 2) / 2;
      const ny = Math.round((w.y - dragRoom.current.offY) * 2) / 2;
      onMoveRoom(dragRoom.current.id, nx, ny);
      return;
    }
    if (!drag.current) return;
    view.current.panX += e.clientX - drag.current.x;
    view.current.panY += e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    forceDraw((n) => n + 1);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!movedFar.current && onSelect) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const rid = hitRoomId(mx, my);
        if (rid) onSelect({ type: 'room', id: rid });
        else {
          const ci = hitCorridorIndex(mx, my);
          onSelect(ci === null ? null : { type: 'corridor', index: ci });
        }
      }
    } else if (dragRoom.current && onSelect) {
      onSelect({ type: 'room', id: dragRoom.current.id });
    }
    drag.current = null;
    dragRoom.current = null;
    downPos.current = null;
  };

  const resetView = () => applyFit();

  return (
    <div
      ref={wrapRef}
      className="relative min-h-[340px] flex-1 overflow-hidden rounded-lg border border-crucible-border"
    >
      <canvas
        ref={canvasRef}
        style={{
          width: size.w,
          height: size.h,
          display: 'block',
          cursor: drag.current ? 'grabbing' : 'crosshair',
        }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
      <button
        type="button"
        onClick={resetView}
        className="absolute right-2 top-2 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-0.5 text-[10px] text-crucible-muted hover:text-crucible-fg"
      >
        fit
      </button>
      <div className="pointer-events-none absolute bottom-2 left-2 text-[10px] text-crucible-muted">
        plan view · scroll = zoom · drag empty = pan · click = select · drag room = move
      </div>
    </div>
  );
}
